import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { RefreshCw, Loader2, CheckCircle2, AlertTriangle, Plug, ExternalLink, LogIn, Unplug, Search, CornerDownLeft } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Line = ({ label, value, testid }) => (
  <div className="flex items-baseline gap-2 py-1 border-b border-[#F4F2ED] last:border-0">
    <span className="text-[11px] uppercase tracking-[0.08em] text-[#8A857A] w-[150px] shrink-0">{label}</span>
    <span className="text-[12.5px] text-[#1A1A1A] break-all" data-testid={testid}>{value}</span>
  </div>
);

const MODE_LABEL = { oauth: "your Atlassian sign-in", api_token: "the shared API token" };

/* What Atlassian actually sent back, shown as-is — this is a connection test,
   not a summary, so nothing here is interpreted or softened. */
const QueryResult = ({ r }) => {
  if (!r) return null;
  if (!r.ok) {
    return (
      <div className="mt-3 rounded-lg border border-[#E8A5A0] bg-[#FDF0EF] p-3" data-testid="triage-query-error">
        <p className="text-[12.5px] font-semibold text-[#8C2F28]">Atlassian refused that read</p>
        <p className="text-[12.5px] text-[#8C2F28] mt-1 break-words">{r.error}</p>
        {r.hint && <p className="text-[12px] text-[#59554D] mt-2">{r.hint}</p>}
      </div>
    );
  }
  const t = r.ticket;
  return (
    <div className="mt-3 rounded-lg border border-[#DAD5CA] bg-[#FBFAF7] p-3" data-testid="triage-query-result">
      <p className="text-[11px] uppercase tracking-[0.08em] text-[#8A857A]">
        {r.kind === "ticket" ? "Ticket read" : "Search"} · {r.tool} · {r.elapsed_ms} ms
        {r.how ? ` · ${r.how}` : ""}
      </p>
      {r.kind === "search" && r.jql && (
        <p className="text-[11.5px] text-[#8A857A] mt-0.5 break-all" data-testid="triage-query-jql">
          It asked Jira: <span className="mono">{r.jql}</span>
        </p>
      )}
      {t && (
        <div className="mt-2">
          <a href={t.url} target="_blank" rel="noopener noreferrer"
             className="text-[13.5px] font-bold text-[#1A1A1A] underline inline-flex items-center gap-1">
            {t.key} <ExternalLink className="w-3 h-3" />
          </a>
          <p className="text-[13px] text-[#1A1A1A] mt-0.5">{t.summary}</p>
          <p className="text-[12px] text-[#59554D] mt-1">
            {[t.status, t.priority, t.organisation, t.assignee, t.updated && `updated ${t.updated}`]
              .filter(Boolean).join(" · ")}
          </p>
          {t.description && (
            <p className="text-[12.5px] text-[#59554D] mt-2 whitespace-pre-wrap break-words">{t.description}</p>
          )}
          <p className="text-[12px] text-[#8A857A] mt-2">{t.comment_count} comment(s) on the thread</p>
          {t.last_comment && (
            <p className="text-[12.5px] text-[#59554D] mt-1 whitespace-pre-wrap break-words">
              Last comment: {t.last_comment}
            </p>
          )}
        </div>
      )}
      {r.kind === "search" && (
        <div className="mt-2 space-y-2">
          {(r.issues || []).length === 0 && (r.documents || []).length === 0 && (
            <div data-testid="triage-query-empty">
              <p className="text-[12.5px] text-[#59554D]">Atlassian found nothing for those words.</p>
              {(r.refused || []).map((x) => (
                <p key={x} className="text-[11.5px] text-[#8A6D00] mt-1 break-words">Jira refused one pass — {x}</p>
              ))}
            </div>
          )}
          {(r.issues || []).map((i) => (
            <div key={i.key} className="border-b border-[#F4F2ED] pb-1.5 last:border-0">
              <a href={i.url} target="_blank" rel="noopener noreferrer"
                 className="text-[12.5px] font-semibold text-[#1A1A1A] underline">{i.key}</a>
              <span className="text-[12.5px] text-[#1A1A1A]"> — {i.summary}</span>
              <p className="text-[11.5px] text-[#8A857A]">
                {[i.status, i.organisation, i.created ? `raised ${i.created.slice(0, 10)}` : null,
                  i.updated ? `updated ${i.updated.slice(0, 10)}` : null].filter(Boolean).join(" · ")}
              </p>
            </div>
          ))}
          {(r.documents || []).map((d) => (
            <div key={d.title} className="text-[12.5px] text-[#59554D]">
              {d.url ? (
                <a href={d.url} target="_blank" rel="noopener noreferrer" className="underline">{d.title}</a>
              ) : d.title}
              {d.space ? ` · ${d.space}` : ""}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* Triage reads Jira and Confluence live, as the person using it, so everyone
   needs to see the state of their own connection — and sign in — from here. */
export default function TriageConnectionCard() {
  const [h, setH] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [question, setQuestion] = useState("");
  const [asking, setAsking] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/triage/health`);
      setH(r.data);
    } catch (e) {
      setH({ connected: false, ready: false, detail: e?.response?.data?.detail || "Health check failed" });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const connect = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/triage/atlassian/connect`);
      window.location.assign(r.data.authorize_url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the Atlassian sign-in");
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/triage/atlassian/disconnect`);
      toast.success("Your Atlassian connection was removed");
      setResult(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not disconnect");
    } finally { setBusy(false); }
  };

  const ask = async () => {
    if (!question.trim()) return;
    setAsking(true);
    setResult(null);
    try {
      const r = await axios.post(`${API}/triage/atlassian/query`, { question: question.trim() });
      setResult(r.data);
    } catch (e) {
      setResult({ ok: false, error: e?.response?.data?.detail || "The request failed" });
    } finally { setAsking(false); }
  };

  const ready = !!h?.ready;
  const ident = h?.identity || {};
  const oauth = h?.oauth || {};
  const mode = h?.auth_mode;

  return (
    <Card className="bg-white border-[#DAD5CA]" data-testid="triage-connection-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <Plug className="w-4 h-4 text-[#59554D]" />
            <div>
              <h3 className="text-[15px] font-bold text-[#1A1A1A]">Atlassian — Jira &amp; Confluence</h3>
              <p className="text-[12.5px] text-[#59554D]">
                Support triage reads Jira Service Management and Confluence live. Everyone signs in
                with their own Atlassian account, so you only ever see what that account can see.
              </p>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={load} disabled={loading}
            className="h-8 border-[#DAD5CA] text-[#59554D] shrink-0" data-testid="triage-health-refresh">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
        </div>

        <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="triage-health-state">
          {ready ? (
            <span className="inline-flex items-center gap-1.5 mono text-[11px] font-bold px-2 py-0.5 rounded-full"
              style={{ background: "#E3F3E9", border: "1px solid #A9CFBB", color: "#0E6B3F" }}>
              <CheckCircle2 className="w-3 h-3" /> READY
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 mono text-[11px] font-bold px-2 py-0.5 rounded-full"
              style={{ background: "#FFF8D6", border: "1px solid #E8C86A", color: "#6B5600" }}>
              <AlertTriangle className="w-3 h-3" /> {(h?.reason || "NOT READY").toUpperCase().replace(/_/g, " ")}
            </span>
          )}
          {mode && (
            <span className="text-[11.5px] text-[#59554D]" data-testid="triage-health-auth-mode">
              reading via {MODE_LABEL[mode] || mode}
            </span>
          )}
        </div>

        {h?.detail && !ready && (
          <div className="mt-2 rounded-lg border border-[#E8C86A] bg-[#FFF8D6] p-2.5"
               data-testid="triage-health-detail">
            <p className="text-[12.5px] text-[#6B5600]">{h.detail}</p>
            {h?.fix_url && (
              <a href={h.fix_url} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-1 mt-2 text-[12px] font-semibold text-[#6B5600] underline"
                 data-testid="triage-health-fix-link">
                Open the Atlassian setting to check <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        )}

        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <Button size="sm" onClick={connect} disabled={busy || h?.can_connect === false}
            className="h-8 bg-[#1A1A1A] hover:bg-[#333] text-white" data-testid="triage-atlassian-connect">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <LogIn className="w-3.5 h-3.5 mr-1.5" />}
            {oauth.connected || oauth.needs_reconnect ? "Reconnect Atlassian" : "Connect my Atlassian account"}
          </Button>
          {(oauth.connected || oauth.needs_reconnect) && (
            <Button size="sm" variant="outline" onClick={disconnect} disabled={busy}
              className="h-8 border-[#DAD5CA] text-[#59554D]" data-testid="triage-atlassian-disconnect">
              <Unplug className="w-3.5 h-3.5 mr-1.5" /> Disconnect
            </Button>
          )}
          <span className="text-[11.5px] text-[#8A857A]">
            Atlassian asks you to grant {h?.site || "the site"} on its own consent screen — that grant
            is the part a shared API token could never get.
          </span>
        </div>

        <div className="mt-3">
          <Line label="Site" value={h?.site || "—"} testid="triage-health-site" />
          <Line label="Sites granted" value={h?.sites_granted ?? "—"} testid="triage-health-sites" />
          <Line label="Reads as"
            value={ident.email || ident.name
              ? [ident.name || null, ident.email || null].filter(Boolean).join(" · ")
              : "unknown until a read succeeds"}
            testid="triage-health-identity" />
          <Line label="Your sign-in"
            value={oauth.needs_reconnect
              ? `${oauth.connected_by || "signed in"} — needs reconnecting`
              : oauth.connected
                ? [oauth.connected_by || null,
                   oauth.connected_at ? new Date(oauth.connected_at).toLocaleString() : null,
                   oauth.refreshable ? "renews itself" : "no refresh token"].filter(Boolean).join(" · ")
                : "you have not connected Atlassian"}
            testid="triage-health-oauth" />
          <Line label="Others connected" value={oauth.others_connected ?? "—"} testid="triage-health-others" />
          <Line label="MCP endpoint" value={oauth.mcp_url || h?.mcp_url || "—"} testid="triage-health-endpoint" />
          <Line label="Cloud id" value={h?.cloud_id || h?.cloud_id_error || "—"} testid="triage-health-cloudid" />
          <Line label="Tools" value={h?.tool_count ? `${h.tool_count} exposed` : "—"} testid="triage-health-tools" />
          <Line label="Missing tools" value={(h?.missing || []).join(", ") || "none"} testid="triage-health-missing" />
        </div>

        {oauth.refresh_error && (
          <p className="mt-2 text-[12px] text-[#8A6D00]" data-testid="triage-health-refresh-error">
            The last token renewal failed ({oauth.refresh_error}). Reconnect to sign in again.
          </p>
        )}

        <div className="mt-5 pt-4 border-t border-[#EDEAE3]">
          <div className="flex items-center gap-2">
            <Search className="w-3.5 h-3.5 text-[#59554D]" />
            <h4 className="text-[13.5px] font-bold text-[#1A1A1A]">Try a read</h4>
          </div>
          <p className="text-[12px] text-[#59554D] mt-0.5">
            Mention a ticket key anywhere — "tell me about SUPPORT-123" reads that ticket. Anything
            else searches Jira and Confluence for those words. You see exactly what Atlassian sent back.
          </p>
          <div className="mt-2 flex items-center gap-2">
            <Input value={question} onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") ask(); }}
              placeholder="Tell me about SUPPORT-123, or licence sync failing"
              className="h-9 border-[#DAD5CA] text-[13px]" data-testid="triage-query-input" />
            <Button size="sm" onClick={ask} disabled={asking || !question.trim()}
              className="h-9 bg-[#1A1A1A] hover:bg-[#333] text-white shrink-0" data-testid="triage-query-submit">
              {asking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CornerDownLeft className="w-3.5 h-3.5" />}
              <span className="ml-1.5">Ask Atlassian</span>
            </Button>
          </div>
          <QueryResult r={result} />
        </div>
      </CardContent>
    </Card>
  );
}
